// @flow

import React, { useCallback, useRef, useMemo, useEffect } from 'react';
import { PromptChatProvider, downloadAsDocx, downloadAsPdf } from '../../ai-prompt-management';
import { IconDownloadDocx, IconDownloadPdf } from '../../ai-prompt-management/components/DownloadIcons';
import { createThrottledTextDispatcher } from '../../ai-prompt-management/utils/throttledDispatch';
import { startChatStream } from './services/chatStreamService';
import { useFeedbackServiceWithMetadata } from '../../ai-prompt-management/context';
import { buildAIChatIntroText } from '../../../utils/ai-chat-questions';

import type { DownloadAction } from '../../ai-prompt-management';

type AIChatWrapperProps = {
  children: React$Node,
  onError?: (error: { type: string, message: string }) => void,
  onThreadIdChange?: (id: string) => void,
  threadId?: string,
  suppressIntroMessage?: boolean,
  staffDetectionDisabled?: boolean,
  demographicsDisabled?: boolean,
  availableMetricGroups?: string[],
  onStreamClose?: () => void,
};

const AIChatWrapper = ({
  children,
  onError,
  onThreadIdChange,
  threadId: threadIdProp,
  suppressIntroMessage = false,
  staffDetectionDisabled = false,
  demographicsDisabled = false,
  availableMetricGroups = [],
  onStreamClose,
}: AIChatWrapperProps) => {
  const feedbackService = useFeedbackServiceWithMetadata();
  const threadIdRef = useRef(threadIdProp);
  const onStreamCloseRef = useRef(onStreamClose);
  onStreamCloseRef.current = onStreamClose;

  const introText = useMemo(
    () => buildAIChatIntroText({ staffDetectionDisabled, demographicsDisabled, availableMetricGroups }),
    [staffDetectionDisabled, demographicsDisabled, availableMetricGroups]
  );
  const streamCancelRef = useRef<?Function>(null);
  const isStreamingRef = useRef<boolean>(false);
  const throttlerCleanupRef = useRef<?Function>(null);
  
  if (threadIdProp && threadIdProp !== threadIdRef.current) {
    threadIdRef.current = threadIdProp;
  }

  const streamingAdapter = useCallback((dispatch) => {
    const { dispatch: throttledDispatch, flush, cleanup } = createThrottledTextDispatcher(dispatch);
    throttlerCleanupRef.current = cleanup;
    
    const send = async (prompt: string, agentMessageId: string) => {
      isStreamingRef.current = true;
      let lastEventText = 'Thinking';
      
      try {
        if (streamCancelRef.current) {
          streamCancelRef.current();
          streamCancelRef.current = null;
        }

        const { cancel } = await startChatStream({
          threadId: threadIdRef.current || '',
          prompt,
          onEvent: (status, data) => {
            if (status === 'event') {
              // Lifecycle event (tool start/stop/error) vs legacy string event
              if (typeof data === 'object' && data !== null && data.phase) {
                if (data.phase === 'start') {
                  const ev = {
                    id: `ev-${Date.now()}-${Math.random().toString(36).slice(2)}`,
                    eventId: data.eventId,
                    text: data.label || '',
                    reason: typeof data.reason === 'string' ? data.reason : undefined,
                    withDots: true,
                    startAt: Date.now(),
                  };
                  throttledDispatch({ type: 'APPEND_EVENT', agentMessageId, event: ev });
                  lastEventText = data.label || '';
                } else if (data.phase === 'stop') {
                  if (!data.eventId) { console.warn('Received stop event without eventId'); }
                  throttledDispatch({ type: 'FINALIZE_EVENT_BY_ID', agentMessageId, eventId: data.eventId });
                } else if (data.phase === 'error') {
                  if (!data.eventId) { console.warn('Received error event without eventId'); }
                  throttledDispatch({ type: 'FINALIZE_EVENT_BY_ID', agentMessageId, eventId: data.eventId, opts: { error: true } });
                }
              } else {
                // Legacy string event
                const eventLabel = typeof data === 'string' ? data : String(data || '');
                // Skip duplicate "Thinking" events
                if (eventLabel.toLowerCase() === 'thinking' && lastEventText.toLowerCase() === 'thinking') {
                  return;
                }
                const ev = {
                  id: `ev-${Date.now()}-${Math.random().toString(36).slice(2)}`,
                  text: eventLabel,
                  withDots: true,
                  startAt: Date.now(),
                };
                throttledDispatch({ type: 'APPEND_EVENT', agentMessageId, event: ev });
                lastEventText = eventLabel;
              }
            } else if (status === 'data-display') {
              // Data display definition from dataDisplayTool
              if (data && data.displayId) {
                const { displayId, ...definition } = data;
                throttledDispatch({
                  type: 'APPEND_DATA_DISPLAY',
                  agentMessageId,
                  displayId,
                  definition,
                });
              }
            } else if (status === 'trace-id') {
              const traceId = data && data.traceId;
              if (traceId) {
                throttledDispatch({ type: 'SET_TRACE_ID', agentMessageId, traceId, sessionId: threadIdRef.current });
              }
            } else if (status === 'agent-message') {
              const msgData = data && typeof data === 'object' ? data.message : data;
              const tokenStr = typeof msgData === 'string' ? msgData : String(msgData || '');
              throttledDispatch({ type: 'APPEND_AGENT_TEXT', agentMessageId, textChunk: tokenStr });
            }
          },
          onError: (message) => {
            flush();
            const errorMessage = String(message || 'An error occurred');
            throttledDispatch({ type: 'FINALIZE_LAST_EVENT', agentMessageId, opts: { error: true } });
            throttledDispatch({ type: 'SET_ERROR', error: { type: 'generic', message: errorMessage } });
            throttledDispatch({ type: 'END_STREAM' });
            isStreamingRef.current = false;
          },
          onClose: () => {
            flush();
            throttledDispatch({ type: 'FINALIZE_LAST_EVENT', agentMessageId });
            throttledDispatch({ type: 'END_STREAM' });
            isStreamingRef.current = false;
            if (onStreamCloseRef.current) onStreamCloseRef.current();
          },
        });

        streamCancelRef.current = cancel;
      } catch (err) {
        dispatch({ type: 'SET_ERROR', error: { type: 'generic', message: err.message || 'Error starting stream' } });
        dispatch({ type: 'END_STREAM' });
        isStreamingRef.current = false;
      }
    };

    const cancel = () => {
      if (streamCancelRef.current) {
        streamCancelRef.current();
        streamCancelRef.current = null;
      }
      isStreamingRef.current = false;
    };

    const reset = () => {
      cancel();
    };

    return {
      send,
      cancel,
      reset,
      get isStreaming() { return isStreamingRef.current; },
    };
  }, []);

  useEffect(() => () => {
    if (streamCancelRef.current) {
      streamCancelRef.current();
      streamCancelRef.current = null;
    }
    if (throttlerCleanupRef.current) {
      throttlerCleanupRef.current();
      throttlerCleanupRef.current = null;
    }
  }, []);

  // Define download actions for AI Chat
  const downloadActions: DownloadAction[] = useMemo(() => [
    {
      id: 'docx',
      icon: <IconDownloadDocx width={16} height={16} />,
      tooltip: 'Download this message as DOCX',
      handler: downloadAsDocx,
    },
    {
      id: 'pdf',
      icon: <IconDownloadPdf width={16} height={16} />,
      tooltip: 'Download this message as PDF',
      handler: downloadAsPdf,
    },
  ], []);

  return (
    <PromptChatProvider
      sessionId={threadIdProp}
      initialAgentText={introText}
      suppressIntroMessage={suppressIntroMessage}
      streamingAdapter={streamingAdapter}
      feedbackService={feedbackService}
      onError={onError}
      onSessionIdChange={onThreadIdChange}
      downloadActions={downloadActions}
    >
      {children}
    </PromptChatProvider>
  );
};

export default AIChatWrapper;
